import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    InputAdornment,
    MenuItem,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    TablePagination,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import dayjs from "dayjs";

import emailLogService from "../../services/emailLogService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

const AdminEmailLogs = () => {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [selected, setSelected] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const loadLogs = async (overrides = {}) => {
        setLoading(true);
        try {
            const { data } = await emailLogService.getAll({
                page: page + 1,
                limit: rowsPerPage,
                search: search || undefined,
                status: status || undefined,
                ...overrides,
            });
            setLogs(data.data || []);
            setTotal(data.pagination?.total || 0);
        } catch {
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
    }, [page, rowsPerPage, status]);

    const handleSearch = (e) => {
        if (e.key === "Enter") {
            setPage(0);
            loadLogs({ page: 1, search: e.target.value });
        }
    };

    const openDetail = async (log) => {
        setDetailLoading(true);
        setSelected(log);
        try {
            const { data } = await emailLogService.getOne(log._id);
            setSelected(data.data.log);
        } catch {
            /* keep list-row data as fallback */
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Email Logs"
                subtitle={`${total} email${total !== 1 ? "s" : ""} sent`}
            />

            <Card>
                <Box
                    sx={{
                        p: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Search recipient, subject…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearch}
                        sx={{ width: { xs: "100%", sm: 260 } }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(0);
                        }}
                        sx={{ width: { xs: "100%", sm: 160 } }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="sent">Sent</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                    </TextField>
                </Box>

                {loading ? (
                    <SkeletonLoader rows={Math.min(8, rowsPerPage)} />
                ) : logs.length === 0 ? (
                    <EmptyState
                        icon="📧"
                        title="No emails found"
                        description="Sent emails will appear here once your app starts sending them."
                    />
                ) : (
                    <>
                        <Box sx={{ overflowX: "auto" }}>
                            <Table sx={{ minWidth: 700 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>To</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Template</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Sent</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.map((log) => (
                                        <TableRow
                                            key={log._id}
                                            hover
                                            onClick={() => openDetail(log)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <TableCell sx={{ fontSize: 13 }}>{log.to}</TableCell>
                                            <TableCell sx={{ maxWidth: 260 }}>
                                                <Typography noWrap variant="body2">
                                                    {log.subject}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                {log.templateKey || "—"}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={log.status}
                                                    size="small"
                                                    color={log.status === "sent" ? "success" : "error"}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                                                {dayjs(log.createdAt).format("MMM D, YYYY h:mm A")}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: 11 }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDetail(log);
                                                    }}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(+e.target.value);
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 20, 50]}
                            sx={{ borderTop: "1px solid", borderColor: "divider" }}
                        />
                    </>
                )}
            </Card>

            <Dialog
                open={!!selected}
                onClose={() => setSelected(null)}
                maxWidth="md"
                fullWidth
            >
                {selected && (
                    <>
                        <DialogTitle
                            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography fontWeight={700} fontSize={15} noWrap>
                                    {selected.subject}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    To: {selected.to} · From: {selected.from}
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setSelected(null)}>
                                <Close fontSize="small" />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    mb: 2,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                }}
                            >
                                <Chip
                                    label={selected.status}
                                    size="small"
                                    color={selected.status === "sent" ? "success" : "error"}
                                />
                                {selected.templateKey && (
                                    <Chip label={selected.templateKey} size="small" variant="outlined" />
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {dayjs(selected.createdAt).format("MMM D, YYYY h:mm A")}
                                </Typography>
                            </Box>

                            {selected.status === "failed" && selected.error && (
                                <Typography
                                    variant="body2"
                                    color="error.main"
                                    sx={{ mb: 2, p: 1.5, bgcolor: "error.light", borderRadius: 2 }}
                                >
                                    {selected.error}
                                </Typography>
                            )}

                            <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                                Body preview
                            </Typography>
                            <Box
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                }}
                            >
                                {detailLoading ? (
                                    <SkeletonLoader rows={4} />
                                ) : (
                                    <iframe
                                        title="email-log-preview"
                                        srcDoc={selected.html || `<pre>${selected.text || ""}</pre>`}
                                        sandbox=""
                                        style={{
                                            width: "100%",
                                            height: 400,
                                            border: "none",
                                            background: "white",
                                        }}
                                    />
                                )}
                            </Box>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default AdminEmailLogs;